/**
 * PlatformTenantsPage — Vista de gestión de tenants para super_admin (Sprint BB).
 *
 * Lista paginada + crear tenant + suspender/reactivar.
 * Tabla simple HTML + Tailwind tokens cumple-* (sin DataTable genérico — BB §5.8).
 * TODO(deuda-tecnica): cuando haya ≥3 vistas con tabla compleja crear DataTable.tsx.
 */
import { type FormEvent, useEffect, useState } from 'react';

import { useAppShell } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton.js';
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../components/ui/Modal.js';
import { useAuth } from '../auth/AuthContext.js';
import { loadSession } from '../auth/storage.js';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  plan: string;
  subscriptionStatus: string | null;
  users: number;
  createdAt: string;
  settings: Record<string, unknown>;
}

interface ListMeta {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────

function getToken(): string {
  // Fuente canónica: sessionStorage via loadSession (evita el bug de leer
  // localStorage['session'] -> token vacío -> 401 missing_token).
  return loadSession()?.token ?? '';
}

async function apiFetch(path: string, opts?: RequestInit): Promise<Response> {
  const token = getToken();
  return fetch(`/api/platform${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts?.headers ?? {}),
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────

export function PlatformTenantsPage(): JSX.Element {
  const { setBackTo } = useAppShell();
  const { session } = useAuth();

  // Back: la plataforma no tiene padre — es raíz para super_admin
  useEffect(() => {
    setBackTo(null);
    return () => setBackTo(null);
  }, [setBackTo]);

  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [meta, setMeta] = useState<ListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'deleted'>(
    'all',
  );
  const [page, setPage] = useState(1);

  // Modal crear
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Modal suspender
  const [suspendTarget, setSuspendTarget] = useState<TenantRow | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);

  // ─── Cargar lista ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!session?.token) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page),
      pageSize: '20',
      status: statusFilter,
    });
    if (search) params.set('search', search);

    apiFetch(`/tenants?${params.toString()}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.json() as Promise<{ data: TenantRow[]; meta: ListMeta }>;
      })
      .then(({ data, meta: m }) => {
        setTenants(data);
        setMeta(m);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error cargando tenants'))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, session?.token]);

  // ─── Crear tenant ───────────────────────────────────────────────────────

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const r = await apiFetch('/tenants', {
        method: 'POST',
        body: JSON.stringify({ name: createName, slug: createSlug, adminEmail: createEmail }),
      });
      if (!r.ok) {
        const body = (await r.json()) as { error: string };
        setCreateError(body.error === 'slug_taken' ? 'El slug ya existe' : body.error);
        return;
      }
      setShowCreate(false);
      setCreateName('');
      setCreateSlug('');
      setCreateEmail('');
      setPage(1);
    } finally {
      setCreating(false);
    }
  }

  // ─── Suspender / Reactivar ──────────────────────────────────────────────

  async function handleStatusChange(
    tenant: TenantRow,
    newStatus: 'suspended' | 'active',
  ): Promise<void> {
    if (newStatus === 'suspended') {
      setSuspendTarget(tenant);
      return;
    }
    // Reactivar: sin confirmación
    await apiFetch(`/tenants/${tenant.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'active' }),
    });
    setPage((p) => p); // fuerza reload
  }

  async function handleSuspendConfirm(): Promise<void> {
    if (!suspendTarget) return;
    setSuspending(true);
    try {
      await apiFetch(`/tenants/${suspendTarget.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'suspended', reason: suspendReason }),
      });
      setSuspendTarget(null);
      setSuspendReason('');
      setPage((p) => p);
    } finally {
      setSuspending(false);
    }
  }

  function tenantStatus(t: TenantRow): string {
    return (t.settings.status as string | undefined) ?? 'active';
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <main className="mx-auto max-w-6xl px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-cumple-primary-800">Tenants</h1>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
          Nuevo tenant
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="search"
          placeholder="Buscar por nombre o slug…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as typeof statusFilter);
            setPage(1);
          }}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="suspended">Suspendido</option>
          <option value="deleted">Eliminado</option>
        </select>
      </div>

      {/* Contenido */}
      {loading ? (
        <LoadingSkeleton variant="list" rows={5} />
      ) : error ? (
        <EmptyState variant="error" title="Error cargando tenants" description={error} />
      ) : tenants.length === 0 ? (
        <EmptyState
          title="Sin tenants"
          description="Crea el primer tenant con el botón de arriba."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-cumple-primary-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Nombre</th>
                  <th className="px-4 py-3 text-left font-medium">Slug</th>
                  <th className="px-4 py-3 text-left font-medium">Plan</th>
                  <th className="px-4 py-3 text-left font-medium">Estado</th>
                  <th className="px-4 py-3 text-right font-medium">Usuarios</th>
                  <th className="px-4 py-3 text-left font-medium">Creado</th>
                  <th className="px-4 py-3 text-left font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tenants.map((t) => {
                  const status = tenantStatus(t);
                  return (
                    <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-cumple-primary-800">{t.name}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{t.slug}</td>
                      <td className="px-4 py-3 text-slate-600">{t.plan}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={status} />
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{t.users}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {new Date(t.createdAt).toLocaleDateString('es-DO')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {status === 'active' ? (
                            <Button
                              variant="danger"
                              size="sm"
                              className="text-xs min-h-[32px]"
                              onClick={() => void handleStatusChange(t, 'suspended')}
                            >
                              Suspender
                            </Button>
                          ) : status === 'suspended' ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="text-xs min-h-[32px]"
                              onClick={() => void handleStatusChange(t, 'active')}
                            >
                              Reactivar
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {meta && meta.pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
              <span>
                Mostrando {(meta.page - 1) * meta.pageSize + 1}–
                {Math.min(meta.page * meta.pageSize, meta.total)} de {meta.total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  className="min-h-[36px]"
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= meta.pages}
                  className="min-h-[36px]"
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal crear tenant */}
      <Modal open={showCreate} onOpenChange={setShowCreate}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Nuevo tenant</ModalTitle>
            <ModalDescription>Crea un tenant y su administrador inicial.</ModalDescription>
          </ModalHeader>
          <form onSubmit={(e) => void handleCreate(e)} className="space-y-4 mt-2">
            <div>
              <label
                htmlFor="create-name"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Nombre
              </label>
              <input
                id="create-name"
                type="text"
                required
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
              />
            </div>
            <div>
              <label
                htmlFor="create-slug"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Slug
              </label>
              <input
                id="create-slug"
                type="text"
                required
                pattern="[a-z0-9-]+"
                value={createSlug}
                onChange={(e) => setCreateSlug(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
              />
              <p className="text-xs text-slate-500 mt-1">Letras minúsculas, números y guiones.</p>
            </div>
            <div>
              <label
                htmlFor="create-email"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Email del admin inicial
              </label>
              <input
                id="create-email"
                type="email"
                required
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
              />
            </div>
            {createError && (
              <p className="text-sm text-red-600" role="alert">
                {createError}
              </p>
            )}
            <ModalFooter>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setShowCreate(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                loading={creating}
                loadingText="Creando…"
              >
                Crear tenant
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Modal confirmar suspensión */}
      <Modal
        open={suspendTarget !== null}
        onOpenChange={(o) => {
          if (!o) setSuspendTarget(null);
        }}
      >
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Suspender tenant</ModalTitle>
            <ModalDescription>
              El tenant <strong>{suspendTarget?.name}</strong> quedará inaccesible para sus
              usuarios.
            </ModalDescription>
          </ModalHeader>
          <div className="mt-2">
            <label
              htmlFor="suspend-reason"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Razón (opcional)
            </label>
            <textarea
              id="suspend-reason"
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-danger-600"
            />
          </div>
          <ModalFooter>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => setSuspendTarget(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={suspending}
              loadingText="Suspendiendo…"
              onClick={() => void handleSuspendConfirm()}
            >
              Suspender
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </main>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const map: Record<string, { label: string; className: string }> = {
    active: { label: 'Activo', className: 'bg-green-100 text-green-800' },
    suspended: { label: 'Suspendido', className: 'bg-yellow-100 text-yellow-800' },
    deleted: { label: 'Eliminado', className: 'bg-red-100 text-red-800' },
  };
  const { label, className } = map[status] ?? {
    label: status,
    className: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${className}`}>
      {label}
    </span>
  );
}
